import i18nRaw, { i18n as I18nInstance } from 'i18next';

import { initReactI18next } from 'react-i18next';
import enUS from './locales/en-US.json';
import zhCN from './locales/zh-CN.json';

/**
 * i18next v26 的 t() 默认签名依赖 CustomTypeOptions.resources 进行严格 key 推断，
 * 当资源 key 是中文原文（且未做精细类型增强）时，TS 会把 t 推断成零参函数，
 * 导致 i18n.t('某中文') 报 TS2554: Expected 0 arguments, but got 1.
 *
 * 这里把 i18next 实例的 t 显式收窄成「接受任意 string，返回 string」的通用签名，
 * 与运行时实际行为完全一致，并避开类型推断陷阱。
 *
 * 注：项目使用 TypeScript 4.3.5，不支持 `import { type X }` inline 类型修饰符，
 * 因此此处使用普通 `import` 引入类型 + 类型断言来兼容。
 */
type LooseT = (key: string, options?: Record<string, unknown>) => string;
type AppI18n = Omit<I18nInstance, 't'> & {
  t: LooseT;
  language: string;
};

const i18n = (i18nRaw as unknown) as AppI18n;

export const SUPPORTED_LOCALES = ['zh-CN', 'en-US'] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: AppLocale = 'zh-CN';
export const LOCALE_STORAGE_KEY = 'lng';

function isSupported(value: string | null | undefined): value is AppLocale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

// 短码 → 完整 locale 归一化（?lng=en → en-US, ?lng=zh → zh-CN）
const SHORT_CODE_MAP: Record<string, AppLocale> = {
  en: 'en-US',
  zh: 'zh-CN',
};

function normalizeLocale(value: string | null | undefined): AppLocale | null {
  if (isSupported(value)) return value;
  if (value && value in SHORT_CODE_MAP) return SHORT_CODE_MAP[value];
  return null;
}

export function syncBodyLocaleClass(locale: AppLocale) {
  if (typeof document === 'undefined') return;
  const sync = () => {
    document.body.classList.remove(...SUPPORTED_LOCALES.map(item => `lang-${item}`));
    document.body.classList.add(`lang-${locale}`);
  };
  if (document.body) {
    sync();
    return;
  }
  document.addEventListener('DOMContentLoaded', sync, { once: true });
}

// 初始语言优先级：URL ?lng= > localStorage > 默认 zh-CN
function detectInitialLocale(): AppLocale {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }
  const fromUrl = new URLSearchParams(window.location.search).get('lng');
  const normalized = normalizeLocale(fromUrl);
  if (normalized) return normalized;

  const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  const savedNormalized = normalizeLocale(saved);
  if (savedNormalized) return savedNormalized;

  return DEFAULT_LOCALE;
}

const initialLocale = detectInitialLocale();
syncBodyLocaleClass(initialLocale);

i18n.use(initReactI18next).init({
  resources: {
    'zh-CN': { translation: zhCN },
    'en-US': { translation: enUS },
  },
  lng: initialLocale,
  fallbackLng: DEFAULT_LOCALE,
  // key 即中文原文，关闭分隔符避免中文里的 . : 被误解析为命名空间/层级
  keySeparator: false,
  nsSeparator: false,
  interpolation: { escapeValue: false },
  // 目标语言缺失该 key 时回退到 fallback(zh-CN)，即显示中文原文
  returnEmptyString: false,
});

export default i18n;
